import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { motion, useReducedMotion } from "framer-motion"
import { ArrowRight, CalendarDays, Plus, Radio } from "lucide-react"
import { api } from "../../../../convex/_generated/api"
import { gameRegistry } from "@/games/registry"
import { useWildGuessSoloEnabled } from "@/lib/analytics/posthog"

export const Route = createFileRoute("/_authed/host/")({
  component: HostHome,
})

function HostHome() {
  const me = useQuery(api.auth.getCurrentUser)
  const events = useQuery(api.events.listMine)
  const activeGames = useQuery(api.games.listMineActive)
  const reduce = useReducedMotion()
  const soloEnabled = useWildGuessSoloEnabled()

  const displayName = me?.displayName ?? me?.phoneNumber ?? me?.email ?? ""
  const upcoming = events?.slice(0, 5) ?? []
  const visibleActiveGames =
    activeGames?.filter(
      (game) => soloEnabled || game.gameType !== "solo-trivia",
    ) ?? []
  const eventCount = events?.length ?? 0

  // Stagger constants — kept tight (40ms gap, 12ms enter) so the list feels
  // alive without making the user wait for it.
  const stagger = reduce ? 0 : 0.04
  const enterDuration = reduce ? 0 : 0.18

  return (
    <div className="space-y-5">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <section className="console-panel rounded-[1.35rem] p-5">
          <p className="console-kicker">Host command center</p>
          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-3xl font-bold tracking-tight">
                Lyrically Challenged nights
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-[color:var(--muted-foreground)]">
                Build the next event, then jump straight into the live host controls.
              </p>
              {displayName && (
                <p className="mt-2 text-xs font-semibold text-[color:var(--muted-foreground)]">
                  Signed in as {displayName}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/host/events/new"
                className="inline-flex min-h-10 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
              >
                <Plus className="h-4 w-4" aria-hidden />
                New event
              </Link>
              <Link
                to="/host/events"
                className="inline-flex min-h-10 items-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-bold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
              >
                <CalendarDays className="h-4 w-4" aria-hidden />
                Events
              </Link>
            </div>
          </div>
        </section>

        <section className="console-panel rounded-[1.35rem] p-4">
          <p className="console-kicker">At a glance</p>
          <dl className="mt-3 grid grid-cols-2 gap-2">
            <MiniMetric label="Run" value={visibleActiveGames.length} />
            <MiniMetric label="Events" value={eventCount} />
          </dl>
          <p className="mt-3 text-xs leading-5 text-[color:var(--muted-foreground)]">
            Host setup stays in web. Player lock-ins happen in the mobile app.
          </p>
        </section>
      </div>

      <section className="console-panel rounded-[1.35rem] p-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="inline-flex items-center gap-2 text-sm font-bold">
            <Radio className="h-4 w-4 text-[color:var(--primary)]" aria-hidden />
            Event running
          </h2>
          <Link
            to="/host/events"
            className="rounded-full px-2 py-1 text-xs font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--surface-soft)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Event list
          </Link>
        </div>
        {activeGames === undefined ? (
          <ListSkeleton rows={2} />
        ) : visibleActiveGames.length > 0 ? (
          <motion.ul
            className="mt-2 space-y-2"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: stagger } },
            }}
          >
            {visibleActiveGames.map((game) => {
              const label =
                gameRegistry[game.gameType as keyof typeof gameRegistry]?.label ??
                game.gameType
              return (
                <motion.li
                  key={game._id}
                  variants={{
                    hidden: { opacity: 0, y: reduce ? 0 : 4 },
                    show: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: enterDuration, ease: "easeOut" },
                    },
                  }}
                >
                  <Link
                    to="/host/games/$gameId"
                    params={{ gameId: game._id }}
                    className="group flex items-center justify-between rounded-2xl border border-primary bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.99]"
                  >
                    <span className="flex items-center gap-2 font-bold">
                      <LivePulse />
                      {label}
                    </span>
                    <span className="text-xs font-bold opacity-90 transition-opacity group-hover:opacity-100">
                      Run game <ArrowRight className="ml-1 inline h-3.5 w-3.5" aria-hidden />
                    </span>
                  </Link>
                </motion.li>
              )
            })}
          </motion.ul>
        ) : (
          <div className="rounded-2xl border border-dashed border-[color:var(--border)] bg-[color:var(--surface-soft)] p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold">No live games</p>
                <p className="mt-1 text-xs leading-5 text-[color:var(--muted-foreground)]">
                  Open registration from Event planning, then run the game here.
                </p>
              </div>
              <Link
                to="/host/events/new"
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                New event
              </Link>
            </div>
          </div>
        )}
      </section>

      <section className="console-panel rounded-[1.35rem] p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold">Recent events</h2>
          <Link
            to="/host/events"
            className="rounded-full px-2 py-1 text-xs font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--surface-soft)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            All events
          </Link>
        </div>
        {events === undefined ? (
          <ListSkeleton rows={3} />
        ) : upcoming.length === 0 ? (
          <EmptyEvents />
        ) : (
          <motion.ul
            className="divide-y divide-[color:var(--border)] overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)]"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: stagger } },
            }}
          >
            {upcoming.map((event) => (
              <motion.li
                key={event._id}
                variants={{
                  hidden: { opacity: 0, y: reduce ? 0 : 4 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: enterDuration, ease: "easeOut" },
                  },
                }}
              >
                <Link
                  to="/host/events/$eventId"
                  params={{ eventId: event._id }}
                  className="group flex items-center justify-between gap-4 px-4 py-3 transition-colors duration-150 hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:bg-[color:var(--muted)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 active:bg-[color:var(--muted)]"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{event.name}</p>
                    <p className="truncate text-xs text-[color:var(--muted-foreground)]">
                      {new Date(event.startsAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                  <StatusPill status={event.status} />
                </Link>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </section>
    </div>
  )
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] p-3">
      <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-[color:var(--muted-foreground)]">
        {label}
      </dt>
      <dd className="mt-1 text-2xl font-bold tabular-nums">{value}</dd>
    </div>
  )
}

function LivePulse() {
  const reduce = useReducedMotion()
  return (
    <span className="relative inline-flex h-2 w-2" aria-hidden>
      {!reduce && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color:var(--accent,theme(colors.lime.300))] opacity-75" />
      )}
      <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
    </span>
  )
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="divide-y divide-[color:var(--border)] rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)]">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className="flex items-center justify-between gap-4 px-4 py-3"
        >
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-[color:var(--muted)]" />
          </div>
          <div className="h-4 w-16 animate-pulse rounded-full bg-[color:var(--muted)]" />
        </li>
      ))}
    </ul>
  )
}

function EmptyEvents() {
  const reduce = useReducedMotion()
  return (
    <motion.div
      initial={{ opacity: 0, y: reduce ? 0 : 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      className="rounded-2xl border border-dashed border-[color:var(--border)] bg-[color:var(--surface-soft)] p-7 text-center"
    >
      <div
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--muted-foreground)]"
        aria-hidden
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 11h18" />
        </svg>
      </div>
      <p className="mt-3 text-sm font-bold">No events yet</p>
      <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
        Schedule your first trivia night to get started.
      </p>
      <Link
        to="/host/events/new"
        className="mt-4 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
      >
        Create your first event
      </Link>
    </motion.div>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone: Record<string, string> = {
    scheduled:
      "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]",
    ready:
      "border-amber-300 bg-amber-50 text-amber-800",
    active:
      "border-emerald-300 bg-emerald-50 text-emerald-800",
    completed:
      "border-[color:var(--border)] bg-[color:var(--muted)] text-[color:var(--muted-foreground)]",
    cancelled: "border-red-200 bg-red-50 text-red-700",
  }
  const cls =
    tone[status] ??
    "border-[color:var(--border)] bg-[color:var(--background)] text-[color:var(--muted-foreground)]"
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${cls}`}
    >
      {status === "cancelled" ? "canceled" : status}
    </span>
  )
}
